import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { authorizedFetch, isAbortError, toEndpointErrorMessage } from "src/network/authorizedFetch";
import { NetworkConfig } from "src/network/NetworkConfig";
import { Role as CustomerRole } from "src/network/User";
import { RootStore } from "./RootStore";

export enum CustomerOverviewModal {
    NONE = 0,
    ACTUALIZE_REPX = 1,
    CLEANUP_FILES = 2,
    UPDATE_API_MINVERSION = 3,
    UPDATE_API_CURRENTVERSION = 4,
    ACTUALIZE_ZIPCODES = 5,
    COPY_CUSTOMER = 6
}

/** Eine Zeile der Kundenübersicht, wie sie der Endpoint von Micro.Customer liefert. */
export interface ICustomerOverviewRow {
    id: string;
    name: string;
    number: string;
    type: number;
    erp: number;
    erpcloud: boolean;
    userCount: number;
}

/** Kunde für die kundenübergreifenden Aktionen; dort werden nur Id und Name gebraucht. */
export interface ICustomerActionTarget {
    id: string;
    name: string;
}

export interface IErpSplitCount {
    rimo: number;
    it2: number;
}

export interface ICustomersOverviewStatistics {
    customersCount: IErpSplitCount;
    usersCount: IErpSplitCount;
    inspectionsCount: IErpSplitCount;
}

/** Fehler einer Datenladung, wie ihn die Kundenübersicht anzeigt. */
export interface ICustomersOverviewError {
    message: string;
}

interface ICustomersOverviewPageResponse {
    items: ICustomerOverviewRow[];
    totalCount: number;
    page: number;
    pageSize: number;
}

interface ICustomersOverviewPage {
    customers: ICustomerOverviewRow[];
    totalEntries: number;
}

export class CustomersOverviewStore {
    static readonly ITEMS_PER_PAGE = 25;

    rootStore: RootStore;

    @observable activeModal: CustomerOverviewModal = CustomerOverviewModal.NONE;
    @observable searchInput = "";
    @observable searchQuery = "";
    @observable currentPage = 1;
    @observable.ref customers: ICustomerOverviewRow[] = [];
    @observable customersTotalEntries = 0;
    @observable customersLoading = false;
    @observable.ref customersError: ICustomersOverviewError | null = null;
    @observable.ref allCustomersForActions: ICustomerActionTarget[] = [];
    @observable allCustomersForActionsLoading = false;
    @observable.ref allCustomersForActionsError: ICustomersOverviewError | null = null;
    @observable.ref statistics: ICustomersOverviewStatistics | undefined = undefined;
    @observable statisticsLoading = false;
    @observable.ref statisticsError: ICustomersOverviewError | null = null;

    private readonly listIdentifier = "customerlist";
    private searchTimeoutId: number | undefined = undefined;

    /**
     * Suche und Seitenwechsel lösen schnell aufeinanderfolgende Ladungen aus. Ohne Abbruch könnte
     * eine langsamere ältere Antwort eine neuere überschreiben.
     */
    private customersRequestAbortController: AbortController | undefined = undefined;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeObservable(this);
    }

    @computed
    get shouldLoadAllCustomersForActions() {
        const { authStore } = this.rootStore;
        const userRole = authStore.user ? authStore.user.role : undefined;

        return userRole === CustomerRole.CUSTOMERDEV || authStore.isSupportUserInRole2ndLevel;
    }

    @action
    initializeFromNavigation = () => {
        const listParameters = this.rootStore.navStore.getListMapValue(this.listIdentifier);

        this.searchInput = listParameters?.searchQuery ?? "";
        this.searchQuery = listParameters?.searchQuery ?? "";
        this.currentPage = listParameters?.paginationPage ?? 1;
    };

    @action
    reset = () => {
        if (this.searchTimeoutId) {
            window.clearTimeout(this.searchTimeoutId);
            this.searchTimeoutId = undefined;
        }

        this.abortRunningCustomersRequest();

        this.activeModal = CustomerOverviewModal.NONE;
        this.customersLoading = false;
        this.customersError = null;
        this.allCustomersForActionsLoading = false;
        this.allCustomersForActionsError = null;
        this.statisticsLoading = false;
        this.statisticsError = null;
    };

    @action
    setActiveModal = (modal: CustomerOverviewModal) => {
        this.activeModal = modal;
    };

    @action
    toggleModal = (modal: CustomerOverviewModal) => {
        this.activeModal = this.activeModal === modal ? CustomerOverviewModal.NONE : modal;
    };

    @action
    handleSearchInput = ({ searchQuery }: { searchQuery?: string }) => {
        const nextSearchQuery = searchQuery ?? "";

        this.searchInput = nextSearchQuery;
        this.currentPage = 1;

        if (this.searchTimeoutId) {
            window.clearTimeout(this.searchTimeoutId);
        }

        this.searchTimeoutId = window.setTimeout(() => {
            runInAction(() => {
                this.searchQuery = this.searchInput;
            });

            this.persistListParameters({ scrollPosition: 0, searchQuery: this.searchInput, paginationPage: 1 });
            this.loadCustomers();
        }, 300);
    };

    @action
    onPageChanged = (nextPage: number) => {
        this.currentPage = nextPage;
        this.persistListParameters({ scrollPosition: window.pageYOffset, paginationPage: nextPage });
        this.loadCustomers();
    };

    loadInitialData = async () => {
        await Promise.all([
            this.loadCustomers(),
            this.loadStatistics(),
            this.shouldLoadAllCustomersForActions ? this.loadAllCustomersForActions() : Promise.resolve()
        ]);
    };

    reloadData = async () => {
        await Promise.all([
            this.loadCustomers(),
            this.loadStatistics(),
            this.shouldLoadAllCustomersForActions ? this.loadAllCustomersForActions() : Promise.resolve()
        ]);
    };

    loadCustomers = async () => {
        this.abortRunningCustomersRequest();

        const abortController = new AbortController();
        this.customersRequestAbortController = abortController;

        runInAction(() => {
            this.customersLoading = true;
            this.customersError = null;
        });

        try {
            const page = await this.fetchCustomers(abortController.signal);

            runInAction(() => {
                this.customers = page.customers;
                this.customersTotalEntries = page.totalEntries;
            });
        } catch (error) {
            if (isAbortError(error)) {
                return;
            }

            runInAction(() => {
                this.customersError = this.toOverviewError(error);
            });
        } finally {
            if (this.customersRequestAbortController === abortController) {
                this.customersRequestAbortController = undefined;

                runInAction(() => {
                    this.customersLoading = false;
                });
            }
        }
    };

    loadAllCustomersForActions = async () => {
        if (!this.shouldLoadAllCustomersForActions) {
            runInAction(() => {
                this.allCustomersForActions = [];
                this.allCustomersForActionsLoading = false;
            });
            return;
        }

        runInAction(() => {
            this.allCustomersForActionsLoading = true;
            this.allCustomersForActionsError = null;
        });

        try {
            const customers = await this.fetchFromEndpoint<ICustomerActionTarget[]>(NetworkConfig.customerNamesUrl);

            runInAction(() => {
                this.allCustomersForActions = customers;
            });
        } catch (error) {
            runInAction(() => {
                this.allCustomersForActionsError = this.toOverviewError(error);
            });
        } finally {
            runInAction(() => {
                this.allCustomersForActionsLoading = false;
            });
        }
    };

    loadStatistics = async () => {
        runInAction(() => {
            this.statisticsLoading = true;
            this.statisticsError = null;
        });

        try {
            const statistics = await this.fetchFromEndpoint<ICustomersOverviewStatistics>(
                NetworkConfig.customersStatisticsUrl
            );

            runInAction(() => {
                this.statistics = statistics;
            });
        } catch (error) {
            runInAction(() => {
                this.statisticsError = this.toOverviewError(error);
            });
        } finally {
            runInAction(() => {
                this.statisticsLoading = false;
            });
        }
    };

    private fetchCustomers = async (signal: AbortSignal): Promise<ICustomersOverviewPage> => {
        const parameters = new URLSearchParams({
            page: this.currentPage.toString(),
            pageSize: CustomersOverviewStore.ITEMS_PER_PAGE.toString()
        });

        const normalizedSearchQuery = this.searchQuery.trim();
        if (normalizedSearchQuery.length > 0) {
            parameters.set("search", normalizedSearchQuery);
        }

        const page = await this.fetchFromEndpoint<ICustomersOverviewPageResponse>(
            `${NetworkConfig.customersOverviewUrl}?${parameters.toString()}`,
            signal
        );

        return {
            customers: page.items ?? [],
            totalEntries: page.totalCount ?? 0
        };
    };

    private fetchFromEndpoint = <TResponse>(url: string, signal?: AbortSignal): Promise<TResponse> =>
        authorizedFetch<TResponse>(this.rootStore.authStore, url, "customers_overview", signal);

    private abortRunningCustomersRequest = () => {
        this.customersRequestAbortController?.abort();
        this.customersRequestAbortController = undefined;
    };

    private toOverviewError = (error: unknown): ICustomersOverviewError => ({
        message: toEndpointErrorMessage(error, "customers_overview")
    });

    private persistListParameters = (nextValues: { scrollPosition?: number; searchQuery?: string; paginationPage?: number }) => {
        const { navStore } = this.rootStore;
        const listParameters = navStore.getListMapValue(this.listIdentifier);

        navStore.setListMapValue(
            this.listIdentifier,
            listParameters ? { ...listParameters, ...nextValues } : { ...nextValues }
        );
    };
}
